import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { voiceKeys, type VoiceFilters } from './useVoices'
import type { VoiceCreateInput, VoiceUpdateInput, VoiceToggleField } from '@/types/voiceAdmin'
import type { Voice } from '@/types/assistant'

/**
 * Voice-admin REST endpoints in one place. Plain REST verbs (no react-admin):
 * GET/POST /voices, GET/PATCH/DELETE /voices/{id}, GET /voices/providers.
 */
export const VOICE_ADMIN_ENDPOINTS = {
  list: '/voices',
  detail: (id: number) => `/voices/${id}`,
  create: '/voices',
  update: (id: number) => `/voices/${id}`,
  delete: (id: number) => `/voices/${id}`,
  providers: '/voices/providers',
} as const

/**
 * Query key for the admin voice list. Nested under voiceKeys.all so the
 * mutations below (which invalidate voiceKeys.all) also refresh it, while
 * staying distinct from the customer picker's voiceKeys.list cache.
 */
const adminVoiceListPrefix = [...voiceKeys.all, 'admin', 'list'] as const

const adminVoiceListKey = (filters: VoiceFilters) => [...adminVoiceListPrefix, filters] as const

const adminVoiceDetailKey = (id: number) => [...voiceKeys.all, 'admin', 'detail', id] as const

async function fetchAdminVoice(id: number): Promise<Voice> {
  const response = await api.get(VOICE_ADMIN_ENDPOINTS.detail(id))
  return response.data
}

export interface VoiceProvider {
  id: number
  name: string
  provider_type: string
  /** Prefill for the admin form's languages field; empty when the provider
   *  has no static default (e.g. Azure, where the voice id decides). */
  default_languages: string[]
  /** Prefill for the admin form's speed score; null when the provider has no
   *  speed baseline. */
  default_speed_score: number | null
}

/**
 * Admin list of all voices (active/inactive, public/private) with relative
 * usage. Distinct from `useVoices`, which the customer-facing picker uses.
 */
export function useAdminVoices(filters: VoiceFilters = {}, enabled = true) {
  return useQuery({
    queryKey: adminVoiceListKey(filters),
    queryFn: async (): Promise<Voice[]> => {
      const params = new URLSearchParams()
      if (filters.language) params.set('language', filters.language)
      if (filters.age_group) params.set('age_group', filters.age_group)
      if (filters.gender) params.set('gender', filters.gender)
      if (filters.tone) params.set('tone', filters.tone)
      if (filters.pricing) params.set('pricing', filters.pricing)
      if (filters.languages?.length) {
        filters.languages.forEach((lang) => params.append('languages', lang))
      }
      if (filters.q) params.set('q', filters.q)
      if (filters.suggested !== undefined) params.set('suggested', String(filters.suggested))

      const query = params.toString()
      const response = await api.get(`${VOICE_ADMIN_ENDPOINTS.list}${query ? `?${query}` : ''}`)
      return response.data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    // Keep showing the previous list while a filter/search refetch is in
    // flight so the page never unmounts back to its loading state.
    placeholderData: keepPreviousData,
  })
}

/**
 * A single voice, always re-read from the server when the query mounts. The
 * form drawer edits against this instead of the list row: `meta` and `config`
 * are replaced whole on save, so a stale row would revert another admin's
 * edit (and re-claim a moved language default).
 */
export function useAdminVoice(id: number | undefined, enabled = true) {
  return useQuery({
    queryKey: adminVoiceDetailKey(id ?? 0),
    queryFn: () => fetchAdminVoice(id as number),
    enabled: enabled && id != null,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Read the list of voice providers (admin-only) used to populate the provider
 * select in the form drawer.
 */
export function useVoiceProviders(enabled = true) {
  return useQuery({
    queryKey: [...voiceKeys.all, 'providers'] as const,
    queryFn: async (): Promise<VoiceProvider[]> => {
      const response = await api.get(VOICE_ADMIN_ENDPOINTS.providers)
      return response.data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Create a new voice. Invalidates the shared voice cache so the customer-facing
 * picker reflects the change.
 */
export function useCreateVoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: VoiceCreateInput): Promise<Voice> => {
      const response = await api.post(VOICE_ADMIN_ENDPOINTS.create, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: voiceKeys.all })
    },
  })
}

/**
 * Update an existing voice. `meta` and `config` are whole-object replacements
 * server-side, so callers send them complete.
 */
export function useUpdateVoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: VoiceUpdateInput): Promise<Voice> => {
      const response = await api.patch(VOICE_ADMIN_ENDPOINTS.update(id), data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: voiceKeys.all })
    },
  })
}

/**
 * Soft-delete a voice. Blocked server-side for the default voice and any voice
 * in use by an assistant.
 */
export function useDeleteVoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await api.delete(VOICE_ADMIN_ENDPOINTS.delete(id))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: voiceKeys.all })
    },
  })
}

/**
 * Toggle a boolean flag (is_public / is_active) in-place via a partial PATCH.
 *
 * The admin list caches are updated optimistically; every voice query is
 * re-fetched on settle so the caches converge on the server state whether the
 * mutation succeeded or failed.
 */
export function useToggleVoiceFlag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      field,
      value,
    }: {
      id: number
      field: VoiceToggleField
      value: boolean
    }): Promise<Voice> => {
      const response = await api.patch(VOICE_ADMIN_ENDPOINTS.update(id), { [field]: value })
      return response.data
    },
    onMutate: async ({ id, field, value }) => {
      await queryClient.cancelQueries({ queryKey: adminVoiceListPrefix })

      queryClient.setQueriesData<Voice[]>({ queryKey: adminVoiceListPrefix }, (voices) =>
        voices?.map((voice) => (voice.id === id ? { ...voice, [field]: value } : voice))
      )
    },
    // Error recovery deliberately has no snapshot rollback: restoring a
    // whole-query snapshot taken in onMutate would also revert the optimistic
    // update of any *other* toggle running concurrently. Instead the cache is
    // left as-is on error and the unconditional invalidation below (which runs
    // on success and error alike) refetches server truth, correcting the
    // failed field along with anything else that drifted.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: voiceKeys.all })
    },
  })
}

/**
 * Toggle the recommended flag (`meta.suggested`) in-place from the admin list.
 * The PATCH endpoint replaces `meta` wholesale and re-runs the language-default
 * claim on every meta write, so the flag is merged onto a fresh read of the
 * voice, never onto the (possibly stale) cached row: a stale `default_for`
 * would otherwise re-claim a language another admin just moved.
 *
 * Optimistic-update strategy mirrors useToggleVoiceFlag: patch the admin list
 * caches immediately, no snapshot rollback on error (that would revert other
 * concurrent toggles), and an unconditional invalidation on settle so the
 * caches converge on server truth whether the mutation succeeded or failed.
 */
export function useToggleVoiceSuggested() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ voice, value }: { voice: Voice; value: boolean }): Promise<Voice> => {
      const current = await fetchAdminVoice(voice.id)
      const meta = { ...current.meta, suggested: value }
      const response = await api.patch(VOICE_ADMIN_ENDPOINTS.update(voice.id), { meta })
      return response.data
    },
    onMutate: async ({ voice, value }) => {
      await queryClient.cancelQueries({ queryKey: adminVoiceListPrefix })

      queryClient.setQueriesData<Voice[]>({ queryKey: adminVoiceListPrefix }, (voices) =>
        voices?.map((v) =>
          v.id === voice.id ? { ...v, meta: { ...v.meta, suggested: value } } : v
        )
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: voiceKeys.all })
    },
  })
}
